import { writable, derived, get } from 'svelte/store';
import type { Writable, Readable } from 'svelte/store';
import en from './translations/en';
import de from './translations/de';

export type Locale = 'en' | 'de';

export const supportedLocales: Locale[] = ['en', 'de'];

export const localeMetadata: Record<Locale, { flag: string; label: string }> = {
  en: { flag: '🇬🇧', label: 'English' },
  de: { flag: '🇩🇪', label: 'Deutsch' },
};

// Derive dot-path key union from the English translation object structure
type PathOf<T, Prefix extends string = ''> = {
  [K in keyof T & string]: T[K] extends Record<string, unknown>
    ? PathOf<T[K], Prefix extends '' ? K : `${Prefix}.${K}`>
    : (Prefix extends '' ? K : `${Prefix}.${K}`)
}[keyof T & string];

export type TranslationKey = PathOf<typeof en>;

const translations: Record<Locale, typeof en> = { en, de };

const STORAGE_KEY = 'locale';

function loadLocale(): Locale {
  const saved = localStorage.getItem(STORAGE_KEY) as Locale | null;
  return saved && supportedLocales.includes(saved) ? saved : 'de';
}

export const locale: Writable<Locale> = writable<Locale>(loadLocale());

locale.subscribe((value) => {
  localStorage.setItem(STORAGE_KEY, value);
});

export const t: Readable<(key: TranslationKey) => string> = derived(
  locale,
  ($locale) =>
    (key: TranslationKey): string => {
      const dict = translations[$locale];
      const parts = (key as string).split('.');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let node: any = dict;
      for (const part of parts) {
        if (node == null || typeof node !== 'object') return key as string;
        node = node[part];
      }
      return typeof node === 'string' ? node : (key as string);
    }
);

/** Non-reactive helper — for use in plain .ts files that need the current locale at call time. */
export function translate(key: TranslationKey): string {
  return get(t)(key);
}
